/**
 * Protected Route Component
 * Wraps routes that require authentication
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  requireModerator?: boolean;
  requirePartner?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  requireModerator = false,
  requirePartner = false,
}) => {
  const { isAuthenticated, isLoading, isAdmin, isSuperAdmin, isModerator, isPartner, refreshUser } = useAuth();
  const location = useLocation();

  // Refresh user data when accessing protected routes to ensure latest role data
  React.useEffect(() => {
    if (isAuthenticated && !isLoading) {
      refreshUser();
    }
  }, [isAuthenticated, isLoading, refreshUser]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/timeline" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/timeline" replace />;
  }

  if (requireModerator && !isModerator && !isAdmin) {
    return <Navigate to="/timeline" replace />;
  }

  if (requirePartner && !isPartner && !isAdmin) {
    return <Navigate to="/timeline" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;

